import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { CONTACT } from '../config/products';
import { PRODUCT_IMAGES } from '../config/images';
import { Button } from './ui/button';
import { MessageCircle, Sparkles, Package, ArrowLeft, ArrowRight } from 'lucide-react';

export const SamplePackCTA = () => {
  const { isRTL } = useLanguage();
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const handleWhatsAppClick = () => {
    const message = isRTL 
      ? 'שלום! אשמח להזמין מארז היכרות של 4 צבעים מינרליים (50 גרם כל אחד)'
      : 'Hello! I would like to order a sample pack of 4 mineral colors (50g each)';
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${encoded}`, '_blank');
  };

  return (
    <section 
      id="sample-pack-section"
      className="py-10 md:py-14 bg-gradient-to-b from-secondary/30 to-background relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 start-0 w-72 h-72 rounded-full bg-gradient-radial from-accent/30 to-transparent blur-3xl" />
        <div className="absolute bottom-1/4 end-0 w-96 h-96 rounded-full bg-gradient-radial from-primary/20 to-transparent blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative order-2 md:order-1"
          >
            <div className="relative">
              {/* Decorative ring */}
              <motion.div
                className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-accent/20 to-primary/10 blur-xl"
                animate={{ scale: [1, 1.02, 1], opacity: [0.5, 0.7, 0.5] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              
              <motion.img
                src={PRODUCT_IMAGES.samplePack}
                alt={isRTL ? 'מארז דוגמיות 4 צבעים' : '4 Colors Sample Pack'}
                className="relative w-full max-w-md mx-auto rounded-2xl shadow-2xl border border-border/50"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />

              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, type: "spring" }}
                className="absolute -top-3 -end-3 bg-accent text-accent-foreground px-4 py-2 rounded-full shadow-lg"
              >
                <span className="font-bold text-sm">50g × 4</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center md:text-start order-1 md:order-2"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/30 mb-6"
            >
              <Sparkles className="w-4 h-4 text-accent" />
              <span className="text-sm font-medium text-accent">
                {isRTL ? 'הכירו את המוצר' : 'Get to Know the Product'}
              </span>
            </motion.div>

            {/* Title */}
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display text-foreground mb-4">
              {isRTL ? 'מארז היכרות' : 'Sample Pack'}
            </h2>

            {/* Subtitle */}
            <p className="text-xl sm:text-2xl text-muted-foreground mb-6">
              {isRTL 
                ? '4 צבעים מינרליים טבעיים' 
                : '4 Natural Mineral Colors'}
            </p>

            {/* Description */}
            <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed">
              {isRTL 
                ? 'מארז דוגמיות של 50 גרם מכל צבע - הדרך המושלמת להכיר את הציפויים המינרליים של מיקרוליט. קבל 4 צבעים רנדומליים כולל מינרלים טבעיים ותוכל לבחון את האיכות והמרקם בעצמך.'
                : 'Sample pack of 50g from each color - the perfect way to get to know Microlit mineral coatings. Get 4 random colors including natural minerals and experience the quality and texture yourself.'}
            </p>

            {/* Features */}
            <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-8">
              {[
                { icon: Package, text: isRTL ? '4 אריזות × 50 גרם' : '4 × 50g packs' },
                { icon: Sparkles, text: isRTL ? 'כולל מינרלים טבעיים' : 'Includes natural minerals' },
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <feature.icon className="w-4 h-4 text-accent" />
                  <span>{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="relative inline-block"
            >
              {/* Glow effect */}
              <motion.div
                className="absolute -inset-1 rounded-2xl bg-success/30 blur-lg"
                animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              
              <Button
                onClick={handleWhatsAppClick}
                size="lg"
                className="relative h-14 px-8 text-lg bg-gradient-to-r from-success to-success/80 hover:from-success/90 hover:to-success/70 text-white shadow-xl shadow-success/25 rounded-xl"
                data-testid="sample-pack-cta-btn"
              >
                <MessageCircle className="w-6 h-6 me-3" />
                <span className="font-semibold">
                  {isRTL ? 'לחץ כאן להזמנה' : 'Click Here to Order'}
                </span>
                <ArrowIcon className="w-5 h-5 ms-3" />
              </Button>
            </motion.div>

            {/* Small note */}
            <p className="mt-4 text-xs text-muted-foreground/70">
              {isRTL 
                ? 'הצבעים נבחרים רנדומלית מתוך מגוון המינרלים הטבעיים' 
                : 'Colors are randomly selected from our natural mineral range'}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
